import { z } from "zod";
import { submissionFilterSchema, submissionSortSchema } from "./submissionQuery.js";

/**
 * The export contract — docs/01-PRODUCT-SPEC.md §9.3, docs/03-API-SPEC.md §7.
 *
 * An export request is a submissions query plus a rendering decision, and it is deliberately the
 * *same* filter shape the table uses: "export the current filter or saved view" is the only thing
 * anyone actually asks for, and a second filter language would drift from the first within a cycle.
 */

export const EXPORT_FORMATS = ["csv", "xlsx", "json", "pdf"] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/** Submission columns that are not answers — offered separately so they can be excluded wholesale. */
export const EXPORT_METADATA_COLUMNS = [
  "reference",
  "randomId",
  "status",
  "reviewStatus",
  "submittedAt",
  "source",
  "ip",
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "duplicateFlag",
  "respondentEmail",
  "respondentName",
  "cycle",
] as const;

export type ExportMetadataColumn = (typeof EXPORT_METADATA_COLUMNS)[number];

export const exportRequestSchema = z
  .object({
    formId: z.string().optional(),
    programmeId: z.string().optional(),
    cycleId: z.string().optional(),
    format: z.enum(EXPORT_FORMATS),

    filters: z.array(submissionFilterSchema).max(25).default([]),
    sort: submissionSortSchema.optional(),
    viewId: z.string().optional(),
    includeTest: z.boolean().default(false),

    /**
     * Include rows the applicant never finished.
     *
     * Off by default, and that default is the fix for a real file: an export of the live Edo form
     * carried 621 rows, of which 155 were abandoned drafts — no reference number, no submitted
     * date, half the answers missing — with nothing in the file to tell them apart, because
     * `status` was not among the default metadata columns either. They read as submissions that
     * had lost their data, and they double-counted anyone who had drafted before submitting.
     *
     * `SUPERSEDED` and `DELETED` rows are excluded with them and have no opt-in: a superseded row
     * is by definition the copy that was replaced.
     */
    includeDrafts: z.boolean().default(false),

    /** Field keys to include, `data.`-prefixed. Empty means every non-sensitive field. */
    columns: z.array(z.string().max(80)).max(300).default([]),
    // `status` is in the default set so a file always says what each row is. Without it, opting
    // into drafts produces a spreadsheet with no way to tell a draft from a submission.
    metadataColumns: z
      .array(z.enum(EXPORT_METADATA_COLUMNS))
      .default(["reference", "status", "submittedAt", "reviewStatus"]),

    /**
     * Headers are the stable field `key` by default — the thing that does not change between
     * cycles and that a downstream spreadsheet formula can rely on. `labelRow` adds the human
     * wording as a second row rather than replacing the first (§9.3).
     */
    headerStyle: z.enum(["key", "label"]).default("key"),
    labelRow: z.boolean().default(false),

    /** Display labels rather than stored values — "Delta" instead of the option's stored code. */
    useDisplayLabels: z.boolean().default(false),

    /** Composite fields become one column per part: `name` → `name.first`, `name.last`, … */
    flattenComposites: z.boolean().default(true),

    /**
     * Include `isSensitive` fields — NIN, bank account number — decrypted, as real columns.
     *
     * Superadmin only, and refused outright for a scheduled export: those run unattended, and
     * nobody is present to hold the permission or to receive the file responsibly. The reason is
     * mandatory and lands in the audit row, because this is the one request that turns a hundred
     * encrypted values into a spreadsheet somebody will email.
     *
     * Default false, so no existing caller starts emitting them by accident.
     */
    includeSensitive: z.boolean().default(false),
    sensitiveReason: z.string().trim().max(500).optional(),

    redactPii: z.boolean().default(false),
  })
  .refine((value) => Boolean(value.formId ?? value.programmeId), {
    message: "one of formId or programmeId is required",
    path: ["formId"],
  });

export type ExportRequest = z.infer<typeof exportRequestSchema>;

/**
 * A Google Sheets export deliberately has no `includeSensitive`. The file leaves our control
 * entirely once it is in someone's Drive — no seven-day expiry, no single-use link, no way to
 * withdraw it — which is the wrong destination for a decrypted NIN under any reason.
 */
export const googleSheetExportSchema = z.object({
  formId: z.string(),
  mode: z.enum(["create", "append", "replace"]),
  spreadsheetId: z.string().optional(),
  sheetName: z.string().max(100).optional(),
  filters: z.array(submissionFilterSchema).max(25).default([]),
  viewId: z.string().optional(),
  columns: z.array(z.string().max(80)).max(300).default([]),
  redactPii: z.boolean().default(false),
  includeTest: z.boolean().default(false),
});

export type GoogleSheetExport = z.infer<typeof googleSheetExportSchema>;

/**
 * A five-field cron, evaluated in the record's own timezone — Africa/Lagos unless someone says
 * otherwise. Validated here rather than at the worker so a mistyped schedule is a 400 at the moment
 * someone saves it, not a job that silently never runs.
 */
const CRON_FIELD = String.raw`(\*|(\d+|\*)(\/\d+)?(-\d+)?)(,(\d+|\*)(\/\d+)?(-\d+)?)*`;
const CRON_PATTERN = new RegExp(`^${CRON_FIELD}( ${CRON_FIELD}){4}$`);

export const scheduledExportSchema = z.object({
  // Required, because the worker cannot run a schedule without it: `scheduledExportProcessor`
  // logs "scheduled export has no form; skipping" and moves on. Accepting a programme-only row
  // meant creating something that would sit in the table looking scheduled and never once fire —
  // better to refuse it at the point someone is present to be told.
  formId: z.string().min(1, "choose the form to export"),
  programmeId: z.string().optional(),
  name: z.string().trim().min(1).max(120),
  cron: z.string().regex(CRON_PATTERN, "not a valid five-field cron expression"),
  timezone: z.string().default("Africa/Lagos"),
  format: z.enum(EXPORT_FORMATS),
  scope: exportRequestSchema.optional(),
  recipients: z.array(z.string().email()).max(50).default([]),
  destination: z.enum(["email", "google_sheets"]).default("email"),
  isActive: z.boolean().default(true),
});

export type ScheduledExportBody = z.infer<typeof scheduledExportSchema>;

export const scheduledExportPatchSchema = scheduledExportSchema.partial();

/** Above this, the request is queued rather than answered inline (docs/03-API-SPEC.md §7). */
export const INLINE_EXPORT_ROW_LIMIT = 1_000;

/** How long a completed export stays downloadable before `export-purge` deletes it (§9.3). */
export const EXPORT_EXPIRY_DAYS = 7;
